import React from 'react';
import { PageHeader, SectionHeader } from '../components/SectionHeader';
import { FormationDiagram } from '../components/FieldDiagram';
import { formationsData } from '../data/nflData';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Lightbulb, Swords, Shield, Zap, Target, Users } from 'lucide-react';

const Strategy = () => {
  return (
    <div className="min-h-screen" data-testid="strategy-page">
      <PageHeader 
        title="Estrategia Básica"
        subtitle="Aprende a ver el juego como un entrenador"
        backgroundImage="https://images.unsplash.com/photo-1547992454-bec8143cc790?auto=format&fit=crop&w=2000&q=80"
      />
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-24">
        
        {/* Intro */}
        <section>
          <SectionHeader 
            title="Piensa como un Coach"
            quarter="INTRO"
            icon={Lightbulb}
          />
          
          <div className="tactical-card p-8 rounded-r-xl">
            <p className="text-lg text-slate-300 leading-relaxed">
              Una vez que entiendes las reglas básicas, el siguiente paso es comprender <strong className="text-white">por qué</strong> los 
              equipos hacen lo que hacen. El fútbol americano es un <strong className="text-emerald-400">juego de ajedrez a alta velocidad</strong>: 
              cada formación, cada movimiento, tiene un propósito estratégico.
            </p>
          </div>
        </section>

        {/* Offensive Plays */}
        <section>
          <SectionHeader 
            title="Tipos de Jugadas Ofensivas"
            quarter="1Q"
            icon={Swords}
          />
          
          <div className="space-y-8">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Run Game */}
              <div className="info-card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                    <Zap className="w-5 h-5 text-blue-400" />
                  </div>
                  <h3 
                    className="text-xl font-bold text-white uppercase"
                    style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                  >
                    Juego de Carrera
                  </h3>
                </div>
                <div className="space-y-4">
                  <div>
                    <h4 className="text-emerald-400 font-semibold mb-1">Inside Run</h4>
                    <p className="text-slate-400 text-sm">Carrera por el centro, entre los guards. Ganancias cortas pero seguras.</p>
                  </div>
                  <div>
                    <h4 className="text-emerald-400 font-semibold mb-1">Outside Run / Sweep</h4>
                    <p className="text-slate-400 text-sm">Carrera hacia los extremos, buscando la esquina. Riesgo de pérdida pero potencial de big play.</p>
                  </div>
                  <div>
                    <h4 className="text-emerald-400 font-semibold mb-1">Draw Play</h4>
                    <p className="text-slate-400 text-sm">El QB simula un pase y luego entrega al RB. Sorprende a defensas agresivas.</p>
                  </div>
                </div>
              </div>
              
              {/* Pass Game */}
              <div className="info-card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                    <Target className="w-5 h-5 text-emerald-400" />
                  </div>
                  <h3 
                    className="text-xl font-bold text-white uppercase"
                    style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                  >
                    Juego de Pase
                  </h3>
                </div>
                <div className="space-y-4">
                  <div>
                    <h4 className="text-orange-400 font-semibold mb-1">Pase Corto (&lt; 10 yardas)</h4>
                    <p className="text-slate-400 text-sm">Slants, screens, quick outs. Alta completación, bajo riesgo.</p>
                  </div>
                  <div>
                    <h4 className="text-orange-400 font-semibold mb-1">Pase Intermedio (10-20 yardas)</h4>
                    <p className="text-slate-400 text-sm">Curls, digs, crossers. Balance entre riesgo y recompensa.</p>
                  </div>
                  <div>
                    <h4 className="text-orange-400 font-semibold mb-1">Pase Profundo (&gt; 20 yardas)</h4>
                    <p className="text-slate-400 text-sm">Go routes, posts. Bajo porcentaje pero pueden cambiar el partido.</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Special Concepts */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="tactical-card p-6 rounded-r-xl">
                <h4 
                  className="text-lg font-bold text-white mb-3 uppercase"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  Play Action
                </h4>
                <p className="text-slate-400 text-sm leading-relaxed">
                  El QB <strong className="text-white">finge entregar</strong> el balón al corredor antes de lanzar. 
                  Esto atrae a los linebackers hacia la línea, dejando huecos en la cobertura de pase.
                </p>
              </div>
              <div className="tactical-card p-6 rounded-r-xl">
                <h4 
                  className="text-lg font-bold text-white mb-3 uppercase"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  RPO (Run-Pass Option)
                </h4>
                <p className="text-slate-400 text-sm leading-relaxed">
                  El QB lee a un defensor específico después del snap y <strong className="text-white">decide en tiempo real</strong> si 
                  entregar a un corredor o lanzar a un receptor. Muy popular en la NFL moderna.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Formations */}
        <section>
          <SectionHeader 
            title="Formaciones"
            quarter="2Q"
            icon={Users}
          />
          
          <p className="text-slate-300 leading-relaxed mb-8">
            Las formaciones determinan <strong className="text-white">dónde se alinean los jugadores</strong> antes del snap. 
            Cada formación tiene fortalezas y debilidades; los entrenadores las eligen según la situación y el plan de juego.
          </p>
          
          <Tabs defaultValue="offense" className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-slate-800 rounded-xl p-1 mb-8">
              <TabsTrigger 
                value="offense" 
                className="data-[state=active]:bg-blue-500 data-[state=active]:text-white rounded-lg font-bold uppercase"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                Formaciones Ofensivas
              </TabsTrigger>
              <TabsTrigger 
                value="defense"
                className="data-[state=active]:bg-red-500 data-[state=active]:text-white rounded-lg font-bold uppercase"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                Formaciones Defensivas
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="offense" className="space-y-6">
              {formationsData.offense.map((formation, index) => (
                <FormationDiagram 
                  key={index}
                  formation={formation}
                  title={formation.name}
                  description={formation.description}
                />
              ))}
            </TabsContent>
            
            <TabsContent value="defense" className="space-y-6">
              {formationsData.defense.map((formation, index) => (
                <FormationDiagram 
                  key={index}
                  formation={formation}
                  title={formation.name}
                  description={formation.description}
                />
              ))}
            </TabsContent>
          </Tabs>
        </section>

        {/* Defensive Concepts */}
        <section>
          <SectionHeader 
            title="Conceptos Defensivos"
            quarter="3Q"
            icon={Shield}
          />
          
          <div className="space-y-8">
            {/* Coverage Types */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="info-card">
                <h3 
                  className="text-xl font-bold text-white mb-4 uppercase flex items-center gap-2"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  <span className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center text-sm text-slate-900">Z</span>
                  Cobertura en Zona
                </h3>
                <p className="text-slate-400 mb-4">
                  Cada defensor es responsable de una <strong className="text-white">área del campo</strong>. 
                  Cubre a cualquier receptor que entre en su zona.
                </p>
                <ul className="space-y-2 text-sm text-slate-400">
                  <li className="flex items-center gap-2">
                    <span className="text-emerald-400">+</span> Ojos en el quarterback
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-emerald-400">+</span> Mejor contra pases cortos
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-red-400">-</span> Huecos entre zonas
                  </li>
                </ul>
              </div>
              
              <div className="info-card">
                <h3 
                  className="text-xl font-bold text-white mb-4 uppercase flex items-center gap-2"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  <span className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center text-sm text-slate-900">M</span>
                  Cobertura Hombre a Hombre
                </h3>
                <p className="text-slate-400 mb-4">
                  Cada defensor sigue a un <strong className="text-white">receptor específico</strong> por todo el campo. 
                  Más agresivo pero más arriesgado.
                </p>
                <ul className="space-y-2 text-sm text-slate-400">
                  <li className="flex items-center gap-2">
                    <span className="text-emerald-400">+</span> Sin huecos en cobertura
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-emerald-400">+</span> Más presión al QB
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-red-400">-</span> Vulnerable a receptores rápidos
                  </li>
                </ul>
              </div>
            </div>
            
            {/* Blitz */}
            <div className="tactical-card p-6 rounded-r-xl" style={{ borderLeftColor: '#f97316' }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
                  <Zap className="w-5 h-5 text-orange-400" />
                </div>
                <h3 
                  className="text-xl font-bold text-white uppercase"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  El Blitz
                </h3>
              </div>
              <p className="text-slate-400 leading-relaxed mb-4">
                Un blitz ocurre cuando la defensa envía <strong className="text-white">más jugadores de lo normal</strong> a 
                presionar al quarterback. Normalmente solo cargan 4 jugadores; en un blitz pueden cargar 5, 6 o más.
              </p>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-slate-800/50 p-4 rounded-lg">
                  <span className="text-emerald-400 font-bold">Ventaja:</span>
                  <p className="text-slate-400 text-sm">Presión rápida, menos tiempo para el QB</p>
                </div>
                <div className="bg-slate-800/50 p-4 rounded-lg">
                  <span className="text-red-400 font-bold">Riesgo:</span>
                  <p className="text-slate-400 text-sm">Menos jugadores en cobertura</p>
                </div>
                <div className="bg-slate-800/50 p-4 rounded-lg">
                  <span className="text-orange-400 font-bold">Cuándo:</span>
                  <p className="text-slate-400 text-sm">3er down, situaciones de pase obvias</p>
                </div>
              </div>
            </div>
            
            {/* Box */}
            <div className="bg-slate-800/50 p-6 rounded-xl border border-slate-700">
              <h4 
                className="text-lg font-bold text-white mb-3 uppercase"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                La "Caja" Defensiva
              </h4>
              <p className="text-slate-400 leading-relaxed">
                Cuando escuches "8 hombres en la caja", significa que hay 8 defensores cerca de la línea de scrimmage. 
                Esto indica que la defensa espera una <strong className="text-white">jugada de carrera</strong> y está 
                sacrificando cobertura de pase para detenerla. Los buenos quarterbacks identifican esto y pueden 
                cambiar a un pase para castigar a la defensa.
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Strategy;
